import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import './Register.css';
import briefcase_img from '../assets/briefcase.png';
import RoleSelection from './RoleSelection';
import CandidateForm from './CandidateForm';
import EmployerForm from './EmployerForm';

const Register = () => {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(null);
  
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelect = (selectedRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setRole(null);
  };

  const handleGoogleSignup = async (credentialResponse) => {
    const backendRole = role === 'candidate' ? 'jobseeker' : 'employer';
    const result = await loginWithGoogle(credentialResponse.credential, backendRole);

    if (result.success) {
      toast.success('Registration successful!');
      navigate('/');
    } else {
      toast.error(result.message || 'Google signup failed');
    }
  };

  const features = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#00c2ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      ),
      title: "Find the right job faster",
      desc: "Personalized job recommendations"
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#00c2ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
      ),
      title: "Build your professional profile",
      desc: "Showcase your skills and experience"
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#00c2ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
        </svg>
      ),
      title: "Apply with one click",
      desc: "Easy and secure application process"
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="#00c2ab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: "Secure & Reliable",
      desc: "Your data is always protected"
    }
  ];

  return (
    <div className="su-page">
      {/* LEFT PANEL */}
      <div className="su-left">
        <span className="su-plus su-plus-1">+</span>
        <span className="su-plus su-plus-2">+</span>

        <div className="su-left-content">
          <h1 className="su-headline">
            Create your<br />
            <span className="su-headline-teal">account</span>
          </h1>
          <p className="su-tagline">
            Join thousands of professionals<br />and grow your career with us.
          </p>

          <div className="su-features">
            {features.map((f, i) => (
              <div className="su-feature-item" key={i} style={{ animationDelay: `${0.1 + i * 0.05}s` }}>
                <div className="su-feature-icon">{f.icon}</div>
                <div>
                  <p className="su-feature-title">{f.title}</p>
                  <p className="su-feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="su-illustration">
          <img src={briefcase_img} alt="Briefcase" className="su-briefcase" />
          <div className="su-plane">
            <svg viewBox="0 0 24 24" fill="#00c2ab" width="38" height="38">
              <path d="M22 2L11 13M22 2L15 22 11 13 2 9l20-7z"/>
            </svg>
          </div>
        </div>

        <div className="su-dot-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="su-dot" />
          ))}
        </div>

        <p className="su-login-link">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>

      {/* RIGHT PANEL */}
      <div className="su-right">
        <div className="su-card">
          
          {step === 1 && <RoleSelection onSelectRole={handleRoleSelect} />}
          {step === 2 && role === 'candidate' && <CandidateForm onBack={handleBack} />}
          {step === 2 && role === 'employer' && <EmployerForm onBack={handleBack} />}

          {/* Social Logins (Only show on form step) */}
          {step === 2 && (
            <div style={{ animation: 'su-fade-in-up 0.3s ease-out forwards', animationDelay: '0.4s', opacity: 0 }}>
              <div className="su-divider"><span>or</span></div>
              <div className="mt-6 flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSignup}
                  onError={() => toast.error('Google signup failed')}
                />
              </div>
              <p className="su-terms">
                By signing up, you agree to our <span>Terms &amp; Privacy Policy</span>
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Register;
